<template>
  <div class="flex h-8 items-center gap-2.5 rounded-ds-md border border-accent-foreground/20 bg-accent py-0 pl-3 pr-1">
    <span class="whitespace-nowrap text-xs font-bold text-accent-foreground">{{ countLabel }}</span>
    <span
      v-if="sumLabel"
      class="whitespace-nowrap text-xs font-medium text-accent-foreground tabular-nums"
    >{{ sumLabel }}</span>
    <DSButton
      variant="ghost"
      size="sm"
      class="h-7 px-2"
      data-copy-button="true"
      @click="handleCopy"
    >
      <Check
        v-if="copied"
        class="size-3.5"
      />
      <Copy
        v-else
        class="size-3.5"
      />
      {{ copied ? 'Copied' : 'Copy' }}
    </DSButton>
    <DSButton
      variant="ghost"
      size="icon-sm"
      class="size-7 ml-auto"
      aria-label="Clear selection"
      @click="$emit('clear')"
    >
      <X class="size-3.5" />
    </DSButton>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { Check, Copy, X } from '@lucide/vue'

import { PivotSelectionSummary } from './hooks/usePivotSelection'

import { numberFormat } from '@/helpers/numbers'

import { DSButton } from '@ds'

type Props = {
  summary: PivotSelectionSummary
  formatSum?: (value: number) => string
}

type Emits = {
  copy: []
  clear: []
}

// Inlined: a defineProps default is hoisted out of setup(), so it can only see true globals.
const { summary, formatSum = (value: number) => numberFormat(Math.round(value), { fractionDigits: 0 }) } = defineProps<Props>()
const emit = defineEmits<Emits>()

const copied = ref(false)

const countLabel = computed(() => `${summary.count} ${summary.count === 1 ? 'cell' : 'cells'}`)

const sumLabel = computed(() => {
  if (summary.sum === undefined) return
  const negative = summary.sum < 0
  return `Sum ${negative ? '−' : ''}${formatSum(Math.abs(summary.sum))}`
})

let copiedTimer: ReturnType<typeof setTimeout>
const handleCopy = () => {
  emit('copy')
  copied.value = true
  clearTimeout(copiedTimer)
  copiedTimer = setTimeout(() => {
    copied.value = false
  }, 1400)
}
</script>
